import React, { useState, useEffect, useCallback } from "react";
import {
  makeStyles, Paper, Typography, Modal,
  TextField, InputAdornment,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import { i18n } from "../../translate/i18n";
import useHelps from "../../hooks/useHelps";

const useStyles = makeStyles(theme => ({
  mainPaperContainer: {
    overflowY: 'auto',
    maxHeight: 'calc(100vh - 200px)',
  },
  mainPaper: {
    width: '100%',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: theme.spacing(3),
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  helpPaper: {
    position: 'relative',
    width: '100%',
    minHeight: '340px',
    padding: theme.spacing(2),
    boxShadow: theme.shadows[3],
    borderRadius: theme.spacing(1),
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    maxWidth: '340px',
  },
  paperHover: {
    transition: 'transform 0.3s, box-shadow 0.3s',
    '&:hover': {
      transform: 'scale(1.03)',
      boxShadow: `0 0 8px`,
      color: theme.palette.primary.main,
    },
  },
  videoThumbnail: {
    width: '100%',
    height: 'calc(100% - 56px)',
    objectFit: 'cover',
    borderRadius: `${theme.spacing(1)}px ${theme.spacing(1)}px 0 0`,
  },
  videoTitle: {
    marginTop: theme.spacing(1),
    flex: 1,
  },
  videoDescription: {
    maxHeight: '100px',
    overflow: 'hidden',
  },
  videoModal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  videoModalContent: {
    outline: 'none',
    width: '90%',
    maxWidth: 1024,
    aspectRatio: '16/9',
    position: 'relative',
    backgroundColor: 'white',
    borderRadius: theme.spacing(1),
    overflow: 'hidden',
  },
  searchField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      fontSize: '0.9rem',
      height: 38,
    },
    minWidth: 260,
  },
  emptyMessage: {
    padding: theme.spacing(4),
    color: '#9b9b9b',
    fontSize: '0.9rem',
    gridColumn: '1 / -1',
  },
}));

const extractYouTubeId = (input) => {
  if (!input) return '';
  if (/^[a-zA-Z0-9_-]{11}$/.test(input.trim())) return input.trim();
  const match = input.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : input.trim();
};

const Helps = () => {
  const classes = useStyles();
  const [records, setRecords] = useState([]);
  const [search, setSearch] = useState('');
  const { list } = useHelps();
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    async function fetchData() {
      const helps = await list();
      setRecords(helps);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openVideoModal = (video) => setSelectedVideo(video);
  const closeVideoModal = () => setSelectedVideo(null);

  const handleModalClose = useCallback((event) => {
    if (event.key === "Escape") closeVideoModal();
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleModalClose);
    return () => document.removeEventListener("keydown", handleModalClose);
  }, [handleModalClose]);

  const filteredRecords = records.filter(record =>
    record.title?.toLowerCase().includes(search.toLowerCase()) ||
    record.description?.toLowerCase().includes(search.toLowerCase())
  );

  const renderVideoModal = () => (
    <Modal
      open={Boolean(selectedVideo)}
      onClose={closeVideoModal}
      className={classes.videoModal}
    >
      <div className={classes.videoModalContent}>
        {selectedVideo && (
          <iframe
            style={{ width: "100%", height: "100%", position: "absolute", top: 0, left: 0 }}
            src={`https://www.youtube.com/embed/${selectedVideo}`}
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        )}
      </div>
    </Modal>
  );

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("helps.title")} ({filteredRecords.length})</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Buscar por nome..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className={classes.searchField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ fontSize: 18, color: '#9b9b9b' }} />
                </InputAdornment>
              ),
            }}
          />
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <div className={classes.mainPaper}>
        <div className={`${classes.mainPaper} ${classes.mainPaperContainer}`}>
          {filteredRecords.length > 0 ? filteredRecords.map((record, key) => (
            <Paper
              key={key}
              className={`${classes.helpPaper} ${classes.paperHover}`}
              onClick={() => openVideoModal(extractYouTubeId(record.video))}
            >
              <img
                src={`https://img.youtube.com/vi/${extractYouTubeId(record.video)}/mqdefault.jpg`}
                alt="Thumbnail"
                className={classes.videoThumbnail}
              />
              <Typography variant="button" className={classes.videoTitle}>
                {record.title}
              </Typography>
              <Typography variant="caption" className={classes.videoDescription}>
                {record.description}
              </Typography>
            </Paper>
          )) : (
            <Typography className={classes.emptyMessage}>
              {search ? `Nenhum resultado para "${search}"` : 'Nenhum conteúdo cadastrado.'}
            </Typography>
          )}
        </div>
      </div>
      {renderVideoModal()}
    </MainContainer>
  );
};

export default Helps;
